import { gql, useQuery } from '@apollo/client';
import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import IconCode from '@apollo/icons/default/IconCode.svg';
import IconMenu from '@apollo/icons/default/IconMenu.svg';
import IconOutlink from '@apollo/icons/small/IconOutlink.svg';
import {
  Button,
  ButtonGroup,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  Heading,
  IconButton,
  Text,
  Tooltip,
} from '@apollo/orbit';
import { Moon } from '@apollo/space-kit/illustrations/Moon';
import { Telescope } from '@apollo/space-kit/illustrations/Telescope';
import classnames from 'classnames';
import _ from 'lodash';
import * as monacoEditor from 'monaco-editor';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';

import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { tolerantParseSchema } from 'src/lib/chevrotain/tolerantParseSchema';
import { getOrSetRecord } from 'src/lib/getOrSetRecord';
import { countAddedAndDeletedTypes } from 'src/lib/graphql/graphqlHelpers';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { DocsRouter } from 'src/lib/routers';

import {
  ProposalCheckRerunButtonWorkflowFragment,
  ProposalsCheckRerunButton,
} from '../../proposalChecksRerunButton/ProposalChecksRerunButton';
import { SUPERGRAPH_SCHEMA_ID } from '../constants';
import {
  LintCheckCodeView,
  // EditorViolationLinterRuleLevelConfigurationFragment, // no rule definitions for custom checks
  // OverrideViolationButtonIgnoredRuleFragment, // can't ignore violations in custom checks
  // OverrideViolationButtonLintDiagnosticFragment, // Only used in lint detail view
} from '../lintCheckDetails/lintCheckCodeView/LintCheckCodeView';
import {
  CustomCheckViolationListFragment,
  LintCheckList,
  LintCheckListCheckWorkflowFragment,
  // LintCheckListLinterRuleLevelConfigurationFragment, // only used in a lint check context
  // OverrideViolationButtonIgnoredRuleFragment, // can't ignore violations in custom checks
  // RuleViolationListLintDiagnosticFragment, // no rule definitions for custom checks
} from '../lintCheckDetails/lintCheckList/LintCheckList';
import { SubgraphTabBar } from '../SubgraphTabBar';
import { useBaseAndProposedSchemas } from '../useBaseAndProposedSchemas';

export function CustomCheckDetails() {
  const { currentCustomCheckDetailsRoute, isProposalRoute } =
    useInternalGraphLinking();
  const {
    graphId,
    graphVariant,
    checkId,
    resultView = 'list',
  } = useRouteParams(currentCustomCheckDetailsRoute);

  const location = useLocation();

  const { data, loading, error } = useQuery<
    GraphQLTypes.CustomCheckDetailsQuery,
    GraphQLTypes.CustomCheckDetailsQueryVariables
  >(
    gql`
      query CustomCheckDetailsQuery($graphId: ID!, $checkId: ID!) {
        service(id: $graphId) {
          id
          checkWorkflow(id: $checkId) {
            id
            status
            baseVariant {
              id
              hasSupergraphSchema
            }
            baseSchemaHash
            proposedSchemaHash
            baseSubgraphs {
              hash
              name
            }
            proposedSubgraphs {
              hash
              name
            }
            ...LintCheckListCheckWorkflowFragment
            ...ProposalCheckRerunButtonWorkflowFragment
            tasks {
              id
              ... on CustomCheckTask {
                result {
                  violations {
                    ...CustomCheckViolationListFragment
                  }
                }
              }
            }
          }
        }
      }
      ${CustomCheckViolationListFragment}
      ${LintCheckListCheckWorkflowFragment}
      ${ProposalCheckRerunButtonWorkflowFragment}
    `,
    {
      variables: { graphId, checkId },
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    },
  );
  const workflow = data?.service?.checkWorkflow;
  const workflowTask = workflow?.tasks.find(
    (task) => task.__typename === 'CustomCheckTask',
  );

  const isSupergraph = !!workflow?.baseVariant?.hasSupergraphSchema;

  // subgraph name or just supergraph
  const [selectedSchema, setSelectedSchema] = useState(SUPERGRAPH_SCHEMA_ID);
  useEffect(() => {
    const firstViolationSchema =
      workflowTask?.__typename === 'CustomCheckTask'
        ? workflowTask.result?.violations[0]?.sourceLocations?.[0]?.subgraphName
        : undefined;

    setSelectedSchema(firstViolationSchema ?? SUPERGRAPH_SCHEMA_ID);
  }, [workflowTask]);

  // The monaco diff editor needs a non empty string to correctly show diffs.
  // if the string is empty, the monaco diff editor uses the last passed sdl &
  // will show an incorrect diff.
  const {
    baseSchema: selectedBaseSdl = ' ',
    proposedSchema: selectedProposedSdl = ' ',
    loading: documentLoading,
  } = useBaseAndProposedSchemas({
    baseSchemaHash:
      selectedSchema === SUPERGRAPH_SCHEMA_ID
        ? workflow?.baseSchemaHash
        : workflow?.baseSubgraphs?.find(
            (subgraph) => subgraph.name === selectedSchema,
          )?.hash,
    proposedSchemaHash:
      selectedSchema === SUPERGRAPH_SCHEMA_ID
        ? workflow?.proposedSchemaHash
        : workflow?.proposedSubgraphs?.find(
            (subgraph) => subgraph.name === selectedSchema,
          )?.hash,
  });

  const { modifiedTypeDefs, originalTypeDefs } = useMemo(() => {
    return {
      modifiedTypeDefs: tolerantParseSchema(
        monacoEditor.editor.createModel(selectedProposedSdl),
      ).documentNode,
      originalTypeDefs: tolerantParseSchema(
        monacoEditor.editor.createModel(selectedBaseSdl),
      ).documentNode,
    };
  }, [selectedBaseSdl, selectedProposedSdl]);

  const typeAndFieldCount = useMemo(
    () =>
      originalTypeDefs && modifiedTypeDefs
        ? countAddedAndDeletedTypes({
            baseDocumentNode: originalTypeDefs,
            modifiedDocumentNode: modifiedTypeDefs,
          })
        : undefined,
    [modifiedTypeDefs, originalTypeDefs],
  );

  const violationsBySubgraph = useMemo(() => {
    const subgraphMaps: Record<
      string,
      Array<GraphQLTypes.CustomCheckViolationListFragment>
    > = {};
    // partition violations by subgraph
    if (workflowTask?.__typename === 'CustomCheckTask') {
      workflowTask.result?.violations.forEach((violation) => {
        const subgraphsAddedForThisDiag = new Set<string>();

        (violation.sourceLocations ?? [{ subgraphName: null }]).forEach(
          (loc) => {
            const subgraphName = loc.subgraphName ?? SUPERGRAPH_SCHEMA_ID;
            if (
              subgraphName === SUPERGRAPH_SCHEMA_ID ||
              !subgraphsAddedForThisDiag.has(subgraphName)
            ) {
              subgraphsAddedForThisDiag.add(subgraphName);
              getOrSetRecord(subgraphName, [], subgraphMaps).push({
                ...violation,
                sourceLocations:
                  violation.sourceLocations?.filter(
                    (l) =>
                      subgraphName === SUPERGRAPH_SCHEMA_ID ||
                      l.subgraphName === subgraphName,
                  ) ?? null,
              });
            }
          },
        );
      });
    }
    return subgraphMaps;
  }, [workflowTask]);

  if (loading) {
    return (
      <div className="m-auto">
        <Loading />
      </div>
    );
  } else if (error) {
    return (
      <div className="p-2">
        <ErrorMessage error={error} />
      </div>
    );
  } else if (!workflow || workflowTask?.__typename !== 'CustomCheckTask') {
    return <NotFound />;
  } else if (!isMinLengthArray(1, workflowTask.result?.violations)) {
    if (workflow.status === GraphQLTypes.CheckWorkflowStatus.FAILED) {
      return (
        <div className="flex flex-1 flex-col items-center justify-center space-y-3 px-6">
          <Telescope className="w-[180px]" />
          <Heading as="h1" size="xl">
            Custom check failed without violations
          </Heading>
          <Text variant="subtitle" className="max-w-2xl text-center">
            The check failed but no violations were included. Please get in
            touch with the maintainer of your custom check for support.
          </Text>
        </div>
      );
    } else {
      return (
        <div className="flex flex-1 flex-col items-center justify-center space-y-3 px-6">
          <Moon className="w-[180px]" />
          <Heading as="h1" size="xl">
            Everything looks good here!
          </Heading>
          <Text variant="subtitle" className="max-w-2xl text-center">
            We found no violations in this check.
          </Text>
        </div>
      );
    }
  }

  return (
    <>
      <CardHeader>
        <CardTitle>
          Check Violations ({workflowTask.result?.violations.length})
        </CardTitle>
        <CardDescription>
          See which check violations caused your workflow to fail.{' '}
          <ClickableText
            as="a"
            href={DocsRouter.path('SchemaValidation')} // TODO(Maya): point to custom checks docs once published
            target="_blank"
            rel="noopener noreferrer"
            className="text-link"
          >
            Learn more
            <IconOutlink className="ml-2" />
          </ClickableText>
        </CardDescription>
        <ButtonGroup>
          {workflow.proposalRevision?.id && graphVariant && (
            <ProposalsCheckRerunButton
              graphId={graphId}
              graphVariant={graphVariant}
              proposalRevisionId={workflow.proposalRevision.id}
              checkWorkflowId={workflow.id}
              trigger={
                <Button
                  size="md"
                  variant="secondary"
                  rightIcon={<IconChevronDown />}
                >
                  Rerun check
                </Button>
              }
            />
          )}
          <Tooltip
            label={isProposalRoute ? 'Show code view' : 'Show diff view'}
          >
            <IconButton
              variant={resultView === 'diff' ? 'primary' : 'secondary'}
              as={Link}
              to={currentCustomCheckDetailsRoute.locationFrom({
                location,
                patch: {
                  resultView: 'diff',
                },
              })}
              aria-label={isProposalRoute ? 'Show code view' : 'Show diff view'}
              aria-controls="diff-view"
              aria-selected={resultView === 'diff'}
            >
              <IconCode />
            </IconButton>
          </Tooltip>
          <Tooltip label="Show list view">
            <IconButton
              variant={resultView === 'list' ? 'primary' : 'secondary'}
              as={Link}
              to={currentCustomCheckDetailsRoute.locationFrom({
                location,
                patch: {
                  resultView: 'list',
                },
              })}
              aria-label="Show list view"
              aria-controls="list-view"
              aria-selected={resultView === 'list'}
            >
              <IconMenu />
            </IconButton>
          </Tooltip>
        </ButtonGroup>
      </CardHeader>
      <CardBody>
        {isSupergraph && (
          <SubgraphTabBar
            schemaNames={Object.keys(violationsBySubgraph)}
            selectedSchema={selectedSchema}
            setSelectedSchema={setSelectedSchema}
          />
        )}

        <div
          id="diff-view"
          className={classnames(resultView !== 'diff' && 'hidden')}
          aria-hidden={resultView !== 'diff'}
        >
          <div className="mr-6">
            <LintCheckCodeView
              violationsBySubgraph={violationsBySubgraph}
              selectedSchema={selectedSchema}
              baseSdl={selectedBaseSdl}
              proposedSdl={selectedProposedSdl}
              typeAndFieldCount={typeAndFieldCount}
              modifiedTypeDefs={modifiedTypeDefs}
              originalTypeDefs={originalTypeDefs}
              documentLoading={documentLoading}
              // rule info isn't used from custom checks
              rules={[]}
              // in custom checks you can't ignore violations
              ignoredLinterViolations={[]}
            />
          </div>
        </div>
        <div
          id="list-view"
          className={classnames(resultView !== 'list' && 'hidden', 'h-full')}
          aria-hidden={resultView !== 'list'}
        >
          <LintCheckList
            violationsBySubgraph={violationsBySubgraph}
            selectedSchema={selectedSchema}
            workflow={workflow}
            typeAndFieldCount={typeAndFieldCount}
            documentLoading={documentLoading}
            // rule info isn't used from custom checks
            rules={[]}
            // in custom checks you can't ignore violations
            ignoredLinterViolations={[]}
            graphId={graphId}
            showMessage={true}
          />
        </div>
      </CardBody>
    </>
  );
}
